import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getSelf } from '../services/client';
import type {
  Disability,
  NenmatsuData,
  NenmatsuDependent,
  NenmatsuInsurance,
  Residency,
} from '../services/types';
import { bi, type Bilingual } from '../i18n/types';
import { BilingualText } from '../components/BilingualText';
import { BrandHeader } from '../components/BrandHeader';

/** このページ専用の JP/VN 文言（共有 dict を肥大化させないため局所定義）。 */
const t = {
  title: bi('年末調整の申告', 'Khai báo điều chỉnh thuế cuối năm'),
  intro: bi(
    'マイナンバーの入力は不要です。送金で養っているベトナムの家族も申告できます。',
    'Không cần nhập mã số cá nhân (My Number). Người thân ở Việt Nam mà bạn gửi tiền chu cấp cũng có thể kê khai.',
  ),
  submitted: bi('提出済みです。修正して再提出もできます。', 'Đã nộp. Bạn có thể sửa và nộp lại.'),
  self: bi('本人の情報', 'Thông tin của bạn'),
  address: bi('住所', 'Địa chỉ'),
  birthDate: bi('生年月日', 'Ngày sinh'),
  householderName: bi('世帯主の氏名', 'Tên chủ hộ'),
  householderRelation: bi('世帯主との続柄（本人・父 等）', 'Quan hệ với chủ hộ (bản thân, cha...)'),
  selfDisability: bi('本人の障害', 'Khuyết tật của bạn'),
  widow: bi('寡婦・ひとり親', 'Góa phụ / Cha mẹ đơn thân'),
  workingStudent: bi('勤労学生', 'Sinh viên vừa học vừa làm'),
  spouseSection: bi('配偶者（結婚相手）', 'Vợ / Chồng'),
  hasSpouse: bi('配偶者はいますか？', 'Bạn có vợ/chồng không?'),
  spouseName: bi('配偶者の氏名', 'Tên vợ/chồng'),
  spouseIncome: bi('配偶者の年間所得見積（円）', 'Thu nhập dự kiến cả năm của vợ/chồng (yên)'),
  selfIncome: bi('あなたの今年の所得見積（円）', 'Thu nhập dự kiến của bạn năm nay (yên)'),
  insuranceSection: bi('保険料控除（証明書の金額）', 'Khấu trừ phí bảo hiểm (số tiền trên giấy chứng nhận)'),
  insuranceHint: bi(
    '保険会社などから届いた控除証明書の金額を入力してください。証明書は会社に提出してください。',
    'Nhập số tiền trên giấy chứng nhận khấu trừ do công ty bảo hiểm gửi. Vui lòng nộp giấy chứng nhận cho công ty.',
  ),
  insLifeGeneral: bi('一般生命保険料', 'Phí bảo hiểm nhân thọ (loại thường)'),
  insLifeCare: bi('介護医療保険料', 'Phí bảo hiểm chăm sóc - y tế'),
  insLifePension: bi('個人年金保険料', 'Phí bảo hiểm hưu trí cá nhân'),
  insEarthquake: bi('地震保険料', 'Phí bảo hiểm động đất'),
  insSocial: bi('社会保険料（給与天引き以外・国民年金等）', 'Phí bảo hiểm xã hội (tự đóng, ngoài lương: bảo hiểm hưu trí quốc dân...)'),
  insSmall: bi('小規模企業共済等（iDeCo 等）', 'Tiền đóng quỹ tương trợ doanh nghiệp nhỏ (iDeCo...)'),
  depSection: bi('扶養親族（養っている家族）', 'Người phụ thuộc (gia đình bạn chu cấp)'),
  addDep: bi('＋ 家族を追加', '+ Thêm người thân'),
  removeDep: bi('削除', 'Xóa'),
  depName: bi('氏名', 'Họ tên'),
  depRelation: bi('続柄（子・父・母 等）', 'Quan hệ (con, cha, mẹ...)'),
  residency: bi('お住まい', 'Nơi ở'),
  income: bi('年間所得見積（円）', 'Thu nhập dự kiến cả năm (yên)'),
  disability: bi('障害', 'Khuyết tật'),
  under16: bi('16歳未満', 'Dưới 16 tuổi'),
  abroadNotice: bi(
    '⚠️ 国外（ベトナム等）の家族を扶養にする場合、「送金の控え」と「親族関係の書類」が必要です。会社にご提出ください。',
    '⚠️ Nếu kê khai người thân ở nước ngoài (Việt Nam...), bạn cần nộp cho công ty “giấy tờ chuyển tiền (chứng từ gửi tiền)” và “giấy tờ chứng minh quan hệ thân nhân”.',
  ),
  note: bi('備考・連絡事項', 'Ghi chú / Liên hệ'),
  saveDraft: bi('一時保存', 'Lưu tạm'),
  submit: bi('提出する', 'Nộp'),
  saved: bi('保存しました', 'Đã lưu'),
  submittedOk: bi('提出しました。ありがとうございました。', 'Đã nộp. Xin cảm ơn.'),
  saveError: bi('保存に失敗しました', 'Lưu thất bại'),
  back: bi('戻る', 'Quay lại'),
  yes: bi('はい', 'Có'),
  no: bi('いいえ', 'Không'),
} satisfies Record<string, Bilingual>;

const RESIDENCY_OPTS: { v: Residency; label: Bilingual }[] = [
  { v: 'together', label: bi('同居', 'Sống chung') },
  { v: 'separate_jp', label: bi('日本国内で別居', 'Sống riêng tại Nhật') },
  { v: 'abroad', label: bi('国外（ベトナム等）', 'Ở nước ngoài (Việt Nam...)') },
];
const DISABILITY_OPTS: { v: Disability; label: Bilingual }[] = [
  { v: 'none', label: bi('なし', 'Không có') },
  { v: 'normal', label: bi('一般', 'Thông thường') },
  { v: 'special', label: bi('特別', 'Đặc biệt') },
];

const emptyDependent = (): NenmatsuDependent => ({
  name: '',
  relation: '',
  birthDate: '',
  residency: 'together',
  incomeEstimate: 0,
  disability: 'none',
  under16: false,
});

const emptyInsurance = (): NenmatsuInsurance => ({
  lifeGeneral: 0,
  lifeCare: 0,
  lifePension: 0,
  earthquake: 0,
  socialInsurance: 0,
  smallMutual: 0,
});

const emptyData = (): NenmatsuData => ({
  address: '',
  birthDate: '',
  householderName: '',
  householderRelation: '',
  selfIncomeEstimate: 0,
  hasSpouse: false,
  spouse: null,
  dependents: [],
  selfDisability: 'none',
  widowOrSingleParent: 'none',
  workingStudent: false,
  insurance: emptyInsurance(),
  note: '',
});

const INSURANCE_FIELDS: { key: keyof NenmatsuInsurance; label: 'insLifeGeneral' | 'insLifeCare' | 'insLifePension' | 'insEarthquake' | 'insSocial' | 'insSmall' }[] = [
  { key: 'lifeGeneral', label: 'insLifeGeneral' },
  { key: 'lifeCare', label: 'insLifeCare' },
  { key: 'lifePension', label: 'insLifePension' },
  { key: 'earthquake', label: 'insEarthquake' },
  { key: 'socialInsurance', label: 'insSocial' },
  { key: 'smallMutual', label: 'insSmall' },
];

const fieldStyle: React.CSSProperties = { display: 'block', width: '100%', padding: 10, fontSize: 16, marginTop: 4, boxSizing: 'border-box' };
const labelStyle: React.CSSProperties = { display: 'block', marginTop: 14, fontWeight: 600 };
const cardStyle: React.CSSProperties = { background: '#fff', borderRadius: 10, padding: 16, marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' };

export function Nenmatsu() {
  const navigate = useNavigate();
  const self = getSelf();
  const [data, setData] = useState<NenmatsuData>(emptyData());
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState<Bilingual | null>(null);

  useEffect(() => {
    if (!self) {
      navigate('/');
      return;
    }
    api
      .getNenmatsu()
      .then((r) => {
        setYear(r.year);
        setSubmitted(r.submitted);
        if (r.data) setData({ ...emptyData(), ...r.data, spouse: r.data.spouse ?? null });
      })
      .catch(() => undefined)
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = <K extends keyof NenmatsuData>(k: K, v: NenmatsuData[K]): void =>
    setData((d) => ({ ...d, [k]: v }));

  const setDep = (i: number, patch: Partial<NenmatsuDependent>): void =>
    setData((d) => ({
      ...d,
      dependents: d.dependents.map((dep, idx) => (idx === i ? { ...dep, ...patch } : dep)),
    }));

  const setIns = (key: keyof NenmatsuInsurance, v: number): void =>
    setData((d) => ({ ...d, insurance: { ...(d.insurance ?? emptyInsurance()), [key]: v } }));

  const anyAbroad =
    (data.hasSpouse && data.spouse?.residency === 'abroad') ||
    data.dependents.some((dep) => dep.residency === 'abroad');

  const save = async (doSubmit: boolean): Promise<void> => {
    setBusy(true);
    setMsg(null);
    try {
      const payload: NenmatsuData = {
        ...data,
        spouse: data.hasSpouse
          ? data.spouse ?? { name: '', birthDate: '', incomeEstimate: 0, residency: 'together', disability: 'none' }
          : null,
      };
      await api.saveNenmatsu(payload, doSubmit, year);
      setSubmitted(doSubmit || submitted);
      setMsg(doSubmit ? t.submittedOk : t.saved);
    } catch {
      setMsg(t.saveError);
    } finally {
      setBusy(false);
    }
  };

  if (!self) return null;

  return (
    <main className="page" style={{ maxWidth: 640, margin: '0 auto', paddingBottom: 40 }}>
      <BrandHeader />
      <header className="page-header" style={{ padding: '12px 16px' }}>
        <button className="link-back" onClick={() => navigate('/punch')}>
          <BilingualText value={t.back} separator="slash" />
        </button>
        <h2 style={{ margin: '8px 0' }}>
          <BilingualText value={t.title} />
        </h2>
        <div style={{ fontSize: 14, color: '#555' }}>
          令和{year - 2018}年分 / {year} ・ {self.name_ja}
        </div>
      </header>

      <div style={{ padding: '0 16px' }}>
        <div style={{ ...cardStyle, background: '#e8f5e9' }}>
          <BilingualText value={t.intro} />
        </div>
        {submitted && (
          <div style={{ ...cardStyle, background: '#fff8e1' }}>
            <BilingualText value={t.submitted} />
          </div>
        )}
        {msg && (
          <div style={{ ...cardStyle, background: '#e3f2fd' }} role="status">
            <BilingualText value={msg} />
          </div>
        )}

        {/* 本人 */}
        <section style={cardStyle}>
          <h3><BilingualText value={t.self} /></h3>
          <label style={labelStyle}><BilingualText value={t.address} separator="slash" />
            <input style={fieldStyle} value={data.address} onChange={(e) => set('address', e.target.value)} />
          </label>
          <label style={labelStyle}><BilingualText value={t.birthDate} separator="slash" />
            <input type="date" style={fieldStyle} value={data.birthDate} onChange={(e) => set('birthDate', e.target.value)} />
          </label>
          <label style={labelStyle}><BilingualText value={t.householderName} separator="slash" />
            <input style={fieldStyle} value={data.householderName} onChange={(e) => set('householderName', e.target.value)} />
          </label>
          <label style={labelStyle}><BilingualText value={t.householderRelation} separator="slash" />
            <input style={fieldStyle} value={data.householderRelation} onChange={(e) => set('householderRelation', e.target.value)} />
          </label>
          <label style={labelStyle}><BilingualText value={t.selfIncome} separator="slash" />
            <input type="number" min={0} style={fieldStyle} value={data.selfIncomeEstimate ?? 0} onChange={(e) => set('selfIncomeEstimate', Number(e.target.value) || 0)} />
          </label>
          <label style={labelStyle}><BilingualText value={t.selfDisability} separator="slash" />
            <select style={fieldStyle} value={data.selfDisability} onChange={(e) => set('selfDisability', e.target.value as Disability)}>
              {DISABILITY_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label.ja} / {o.label.vi}</option>)}
            </select>
          </label>
          <label style={labelStyle}><BilingualText value={t.widow} separator="slash" />
            <select style={fieldStyle} value={data.widowOrSingleParent} onChange={(e) => set('widowOrSingleParent', e.target.value as NenmatsuData['widowOrSingleParent'])}>
              <option value="none">なし / Không có</option>
              <option value="widow">寡婦 / Góa phụ (góa vợ/chồng)</option>
              <option value="single_parent">ひとり親 / Cha/mẹ đơn thân</option>
            </select>
          </label>
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={data.workingStudent} onChange={(e) => set('workingStudent', e.target.checked)} />
            <BilingualText value={t.workingStudent} separator="slash" />
          </label>
        </section>

        {/* 配偶者 */}
        <section style={cardStyle}>
          <h3><BilingualText value={t.spouseSection} /></h3>
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={data.hasSpouse} onChange={(e) => set('hasSpouse', e.target.checked)} />
            <BilingualText value={t.hasSpouse} separator="slash" />
          </label>
          {data.hasSpouse && (
            <>
              <label style={labelStyle}><BilingualText value={t.spouseName} separator="slash" />
                <input style={fieldStyle} value={data.spouse?.name ?? ''} onChange={(e) => set('spouse', { ...(data.spouse ?? { name: '', birthDate: '', incomeEstimate: 0, residency: 'together', disability: 'none' }), name: e.target.value })} />
              </label>
              <label style={labelStyle}><BilingualText value={t.birthDate} separator="slash" />
                <input type="date" style={fieldStyle} value={data.spouse?.birthDate ?? ''} onChange={(e) => set('spouse', { ...(data.spouse ?? { name: '', birthDate: '', incomeEstimate: 0, residency: 'together', disability: 'none' }), birthDate: e.target.value })} />
              </label>
              <label style={labelStyle}><BilingualText value={t.spouseIncome} separator="slash" />
                <input type="number" min={0} style={fieldStyle} value={data.spouse?.incomeEstimate ?? 0} onChange={(e) => set('spouse', { ...(data.spouse ?? { name: '', birthDate: '', incomeEstimate: 0, residency: 'together', disability: 'none' }), incomeEstimate: Number(e.target.value) || 0 })} />
              </label>
              <label style={labelStyle}><BilingualText value={t.residency} separator="slash" />
                <select style={fieldStyle} value={data.spouse?.residency ?? 'together'} onChange={(e) => set('spouse', { ...(data.spouse ?? { name: '', birthDate: '', incomeEstimate: 0, residency: 'together', disability: 'none' }), residency: e.target.value as Residency })}>
                  {RESIDENCY_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label.ja} / {o.label.vi}</option>)}
                </select>
              </label>
            </>
          )}
        </section>

        {/* 扶養親族 */}
        <section style={cardStyle}>
          <h3><BilingualText value={t.depSection} /></h3>
          {data.dependents.map((dep, i) => (
            <div key={i} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12, marginTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>#{i + 1}</strong>
                <button className="link-back" onClick={() => set('dependents', data.dependents.filter((_, idx) => idx !== i))}>
                  <BilingualText value={t.removeDep} separator="slash" />
                </button>
              </div>
              <label style={labelStyle}><BilingualText value={t.depName} separator="slash" />
                <input style={fieldStyle} value={dep.name} onChange={(e) => setDep(i, { name: e.target.value })} />
              </label>
              <label style={labelStyle}><BilingualText value={t.depRelation} separator="slash" />
                <input style={fieldStyle} value={dep.relation} onChange={(e) => setDep(i, { relation: e.target.value })} />
              </label>
              <label style={labelStyle}><BilingualText value={t.birthDate} separator="slash" />
                <input type="date" style={fieldStyle} value={dep.birthDate} onChange={(e) => setDep(i, { birthDate: e.target.value })} />
              </label>
              <label style={labelStyle}><BilingualText value={t.residency} separator="slash" />
                <select style={fieldStyle} value={dep.residency} onChange={(e) => setDep(i, { residency: e.target.value as Residency })}>
                  {RESIDENCY_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label.ja} / {o.label.vi}</option>)}
                </select>
              </label>
              <label style={labelStyle}><BilingualText value={t.income} separator="slash" />
                <input type="number" min={0} style={fieldStyle} value={dep.incomeEstimate} onChange={(e) => setDep(i, { incomeEstimate: Number(e.target.value) || 0 })} />
              </label>
              <label style={labelStyle}><BilingualText value={t.disability} separator="slash" />
                <select style={fieldStyle} value={dep.disability} onChange={(e) => setDep(i, { disability: e.target.value as Disability })}>
                  {DISABILITY_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label.ja} / {o.label.vi}</option>)}
                </select>
              </label>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={dep.under16} onChange={(e) => setDep(i, { under16: e.target.checked })} />
                <BilingualText value={t.under16} separator="slash" />
              </label>
            </div>
          ))}
          <button className="btn btn-secondary btn-md" style={{ marginTop: 12 }} onClick={() => set('dependents', [...data.dependents, emptyDependent()])}>
            <BilingualText value={t.addDep} separator="slash" />
          </button>
          {anyAbroad && (
            <div style={{ ...cardStyle, background: '#fff3e0', marginTop: 12 }}>
              <BilingualText value={t.abroadNotice} />
            </div>
          )}
        </section>

        {/* 保険料控除 */}
        <section style={cardStyle}>
          <h3><BilingualText value={t.insuranceSection} /></h3>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>
            <BilingualText value={t.insuranceHint} />
          </div>
          {INSURANCE_FIELDS.map((f) => (
            <label key={f.key} style={labelStyle}><BilingualText value={t[f.label]} separator="slash" />
              <input
                type="number"
                min={0}
                style={fieldStyle}
                value={data.insurance?.[f.key] ?? 0}
                onChange={(e) => setIns(f.key, Number(e.target.value) || 0)}
              />
            </label>
          ))}
        </section>

        {/* 備考 */}
        <section style={cardStyle}>
          <label style={labelStyle}><BilingualText value={t.note} separator="slash" />
            <textarea style={{ ...fieldStyle, minHeight: 80 }} value={data.note} onChange={(e) => set('note', e.target.value)} />
          </label>
        </section>

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <button className="btn btn-secondary btn-lg" style={{ flex: 1 }} disabled={busy} onClick={() => save(false)}>
            <BilingualText value={t.saveDraft} separator="slash" />
          </button>
          <button className="btn btn-success btn-lg" style={{ flex: 1 }} disabled={busy} onClick={() => save(true)}>
            <BilingualText value={t.submit} separator="slash" />
          </button>
        </div>
      </div>
    </main>
  );
}
